import React, { memo, useMemo } from 'react'
import VideoCard from './VideoCard'
import { EnrichedInterviewData } from '../../hooks/useInterviewManagement'
import { useNavigate } from 'react-router-dom';
import { setWithExpiry } from '../../utils/sessionStorage'
import Spinner from '../Spinner'
import { isExpandedInterviewOverlayFeatureEnabled } from '../../utils/featureFlags'
import { DeleteReason } from '../../types/interview'

interface VideoData {
  id: string;
  applicantName: string;
  jobTitle: string;
  dateApplied: string;
  thumbnailUrl?: string;
  isMobile?: boolean;
  interview: EnrichedInterviewData;
  // VI-468: forwarded from InterviewsPage's videoData mapper. When the
  // backing interview has is_deleted=true, the card renders a placeholder
  // instead of the thumbnail/play UI.
  isDeleted?: boolean;
  deleteReason?: DeleteReason | null;
}

interface VideoGridProps {
  videos: VideoData[];
  maxCards?: number;
  // VI-623: offset into `videos` for client-side pagination. The grid renders
  // videos[startIndex .. startIndex + maxCards); the overlay/navigation still
  // receives the full `videos` array so paging doesn't limit it.
  startIndex?: number;
  isMobile?: boolean;
  isLoading?: boolean;
  selectedJob?: any;
  selectedDateRange?: string;
  appliedQuestions?: number | null;
  allInterviews?: any[]; // All loaded interviews from useInterviewManagement
  currentPage?: number;
  hasLoadedAll?: boolean;
  totalInterviews?: number;
  onVideoCardClick?: (video: any, allVideos: any[]) => void;
}

const VideoGrid: React.FC<VideoGridProps> = memo(({
  videos,
  maxCards = 9,
  startIndex = 0,
  isMobile = false,
  isLoading = false,
  selectedJob,
  selectedDateRange,
  appliedQuestions,
  allInterviews,
  currentPage,
  hasLoadedAll,
  totalInterviews,
  onVideoCardClick
 }) => {
  const navigate = useNavigate();
  // Memoize displayed videos to prevent unnecessary recalculations
  const displayVideos = useMemo(
    () => videos.slice(startIndex, startIndex + maxCards),
    [videos, startIndex, maxCards]
  );

  // Memoize grid columns calculation
  const gridColumns = useMemo(() => {
    return isMobile ? 'repeat(1, 1fr)' : 'repeat(3, 1fr)';
  }, [isMobile]);

  // Memoize skeleton cards
  const skeletonCards = useMemo(() => Array(maxCards).fill(null), [maxCards]);

  // Show skeleton cards while loading
  if (isLoading) {
    // for now align with JT ui library
    return <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      minHeight: '2rem', // Takes at least half the viewport height
      width: '100%'
    }}>
      <Spinner size={isMobile ? 'md' : 'xl'} />
    </div>
    
    
    // return (
    //   <div
    //     style={{
    //       display: 'grid',
    //       gridTemplateColumns: gridColumns,
    //       gap: isMobile ? '0.5rem' : '0.8rem',
    //       width: '100%',
    //     }}
    //   >
    //     {skeletonCards.map((_, index) => (
    //       <div
    //         key={`skeleton-${index}`}
    //         style={{
    //           marginBottom: isMobile ? '0.5rem' : '0.8rem'
    //         }}
    //       >
    //         <VideoCard isLoading={true} isMobile={isMobile} />
    //       </div>
    //     ))}
    //   </div>
    // );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: gridColumns,
        gap: isMobile ? '0.5rem' : '0.8rem',
        width: '100%',
      }}
    >
      {displayVideos.map((video) => (
        <div
          key={video.id} // Stable key based on video ID
          style={{
            marginBottom: isMobile ? '0.5rem' : '0.8rem'
          }}
        >
          <VideoCard 
            applicantName={video.applicantName}
            jobTitle={video.jobTitle}
            dateApplied={video.dateApplied}
            thumbnailUrl={video.thumbnailUrl}
            isMobile={isMobile}
            videoId={video.id} // Pass stable ID for memoization
            isDeleted={video.isDeleted}
            deleteReason={video.deleteReason}
            onClick={() => {
              const cacheData = {
                allVideos: videos,
                selectedVideo: video,
                filters: {
                  selectedJob,
                  selectedDateRange,
                  appliedQuestions
                },
                interviewDataState: {
                  allInterviews: allInterviews || [],
                  currentPage: currentPage || 1,
                  hasLoadedAll: hasLoadedAll || false,
                  totalInterviews: totalInterviews || 0,
                  displayedCards: maxCards || 9
                }
              };
              
              // Check if overlay feature is enabled
              if (isExpandedInterviewOverlayFeatureEnabled() && onVideoCardClick) {
                // Use overlay
                console.log('Opening overlay for video:', video);
                onVideoCardClick(video, videos);
              } else {
                // Use traditional navigation
                setWithExpiry('expandedInterviewData', cacheData, 1800000);
                console.log('Video clicked:', video);
                console.log('Full interview object:', video.interview);
                navigate(`/v1/recruiter/interview/${video.id}`, {
                  state: {
                    selectedVideo: video,
                    allVideos: videos, 
                  }
                });
              }
            }}
          />
        </div>
      ))}
    </div>
  )
});

VideoGrid.displayName = 'VideoGrid';

export default VideoGrid